import React, { useEffect, useState, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const WEEKDAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];
const RULE_LABELS = { weekend: "Weekend", seasonal: "Seasonal", holiday: "Holiday" };

function RateCalendarView() {
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dayPrice, setDayPrice] = useState("");
  const load = useCallback(() => {
    if (roomId && month) api.get("/admin/rate-calendar", { params: { room_id: roomId, month } }).then((r) => setData(r.data)).catch(() => {});
  }, [roomId, month]);
  useEffect(() => { api.get("/rooms").then((r) => { setRooms(r.data); setRoomId((id) => id || r.data[0]?.id || ""); }); }, []);
  useEffect(() => { load(); }, [load]);
  const dayAction = async (action, price) => {
    if (!selected) return;
    try {
      await api.post("/admin/rate-calendar/day", { room_id: roomId, date: selected.date, action, price: Number(price || 0) });
      toast.success("Calendar updated");
      setSelected(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cellStyle = (d) => {
    if (d.blocked) return "bg-stone-300 text-stone-500 line-through";
    if (d.booked) return "bg-forest text-cream";
    if (d.type === "holiday") return "bg-rose/15 text-rose";
    if (d.type === "seasonal") return "bg-blue-100 text-blue-700";
    if (d.type === "weekend") return "bg-gold/20 text-[#8a6a2f]";
    return "bg-white text-stone-600 border border-stone-100";
  };
  const lead = data?.days?.[0] ? data.days[0].weekday : 0;
  return (
    <div className="card-lux p-6" data-testid="rate-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-serif text-xl text-forest">Rate calendar</h3>
        <div className="flex gap-2">
          <select value={roomId} onChange={(e) => setRoomId(e.target.value)} data-testid="rate-cal-room" className="rounded-lg border border-stone-200 px-3 py-2 text-sm">
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="rate-cal-month" className="rounded-lg border border-stone-200 px-3 py-2 text-sm" />
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        {[["Base", "bg-white border border-stone-200"], ["Weekend", "bg-gold/20"], ["Seasonal", "bg-blue-100"], ["Holiday", "bg-rose/15"], ["Booked", "bg-forest"], ["Blocked", "bg-stone-300"]].map(([l, c]) => (
          <span key={l} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded ${c}`} /> {l}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-center text-[11px] font-medium text-stone-400 pb-1">{d}</div>)}
        {Array.from({ length: lead }).map((_, i) => <div key={`b${i}`} />)}
        {data?.days.map((d) => (
          <button type="button" key={d.date} data-testid={`cal-day-${d.date}`} onClick={() => { setSelected(d); setDayPrice(d.price); }}
            className={`rounded-lg p-2 text-center transition-transform hover:scale-[1.04] ${cellStyle(d)} ${selected?.date === d.date ? "ring-2 ring-rose" : ""}`}
            title={`${d.date} · ${d.type}${d.booked ? " · booked" : ""}${d.blocked ? " · blocked" : ""}`}>
            <div className="text-[11px] opacity-70">{d.day}</div>
            <div className="text-xs font-semibold">${d.price}</div>
          </button>
        ))}
      </div>
      {selected && (
        <div className="mt-4 border-t border-stone-100 pt-4" data-testid="cal-day-editor">
          <div className="flex flex-wrap items-end gap-3">
            <div className="text-sm text-stone-600">Editing <strong>{selected.date}</strong></div>
            <label className="text-sm">Price/night <input type="number" value={dayPrice} onChange={(e) => setDayPrice(e.target.value)} data-testid="cal-day-price" className="w-24 rounded-lg border border-stone-200 px-3 py-2 ml-1" /></label>
            <button onClick={() => dayAction("set_price", dayPrice)} data-testid="cal-set-price" className="btn-primary py-2 px-4 text-xs">Set price</button>
            <button onClick={() => dayAction("clear_price")} className="text-xs px-3 py-2 rounded-full bg-stone-100 text-stone-600">Reset to base</button>
            {selected.blocked
              ? <button onClick={() => dayAction("unblock")} data-testid="cal-unblock" className="text-xs px-3 py-2 rounded-full bg-green-50 text-green-700">Unblock day</button>
              : <button onClick={() => dayAction("block")} data-testid="cal-block" className="text-xs px-3 py-2 rounded-full bg-red-50 text-red-600">Block day</button>}
            <button onClick={() => setSelected(null)} className="text-xs px-3 py-2 rounded-full bg-stone-100 text-stone-500">Close</button>
          </div>
        </div>
      )}
      {data && <div className="text-xs text-stone-400 mt-3">Base rate ${data.base}/night · {data.room}</div>}
    </div>
  );
}

export default function RatesAdmin() {
  const [rooms, setRooms] = useState([]);
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState({ label: "", room_id: "all", type: "weekend", mode: "percent", value: 20, start: "", end: "", weekdays: [4, 5], min_stay: 0 });
  const load = useCallback(() => {
    api.get("/rooms").then((r) => setRooms(r.data));
    api.get("/admin/rate-rules").then((r) => setRules(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleDay = (d) => setForm((f) => ({ ...f, weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d] }));

  const add = async (e) => {
    e.preventDefault();
    if (form.type !== "weekend" && (!form.start || !form.end)) { toast.error("Set a start and end date for this rule"); return; }
    try {
      await api.post("/admin/rate-rules", { ...form, value: Number(form.value), min_stay: Number(form.min_stay) });
      toast.success("Rate rule saved");
      setForm({ label: "", room_id: "all", type: "weekend", mode: "percent", value: 20, start: "", end: "", weekdays: [4, 5], min_stay: 0 });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/rate-rules/${id}`); load(); };
  const roomName = (id) => id === "all" ? "All suites" : rooms.find((r) => r.id === id)?.name || "Suite";

  return (
    <div className="space-y-6">
      <RateCalendarView />
      <div className="grid lg:grid-cols-3 gap-6">
      <form onSubmit={add} className="card-lux p-6 h-fit space-y-3" data-testid="rate-rule-form">
        <h3 className="font-serif text-xl text-forest mb-1">New rate rule</h3>
        <p className="text-xs text-stone-500 mb-2">Adjust nightly pricing and set minimum-stay rules by suite.</p>
        <input required placeholder="Label (e.g. Peak Season)" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" />
        <select value={form.room_id} onChange={(e) => setForm({ ...form, room_id: e.target.value })} className="w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
          <option value="all">All suites</option>
          {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-3">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="rate-type" className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
            <option value="weekend">Weekend</option><option value="seasonal">Seasonal</option><option value="holiday">Holiday</option>
          </select>
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="rounded-lg border border-stone-200 px-3 py-2.5 text-sm">
            <option value="percent">% change</option><option value="override">Fixed $/night</option>
          </select>
        </div>
        <label className="text-sm block">{form.mode === "percent" ? "Percent uplift (e.g. 20)" : "Nightly price ($)"}
          <input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5" /></label>
        {form.type === "weekend" ? (
          <div>
            <span className="text-xs uppercase tracking-wide text-stone-500">Applies on</span>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {WEEKDAYS.map(([lbl, d]) => (
                <button type="button" key={d} onClick={() => toggleDay(d)} className={`px-2.5 py-1 rounded-full text-xs ${form.weekdays.includes(d) ? "bg-rose text-white" : "bg-stone-100 text-stone-600"}`}>{lbl}</button>
              ))}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-stone-500">From<input type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" /></label>
            <label className="text-xs text-stone-500">To<input type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5 text-sm" /></label>
          </div>
        )}
        <label className="text-sm block">Minimum stay (nights, 0 = none)
          <input type="number" min={0} value={form.min_stay} onChange={(e) => setForm({ ...form, min_stay: e.target.value })} data-testid="rate-min-stay" className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2.5" /></label>
        <button className="btn-primary w-full"><Plus size={15} /> Save rule</button>
      </form>

      <div className="lg:col-span-2 space-y-3">
        {rules.map((r) => (
          <div key={r.id} className="card-lux p-4 flex items-center justify-between" data-testid={`rate-rule-${r.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-forest">{r.label}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-gold/20 text-gold">{RULE_LABELS[r.type]}</span>
                <span className="text-xs text-stone-400">{roomName(r.room_id)}</span>
              </div>
              <div className="text-sm text-stone-500 mt-1">
                {r.mode === "percent" ? `${r.value > 0 ? "+" : ""}${r.value}% nightly` : `$${r.value}/night`}
                {r.type === "weekend" ? ` · ${r.weekdays.map((d) => WEEKDAYS.find(([, x]) => x === d)?.[0]).join(", ")}` : ` · ${r.start} → ${r.end}`}
                {r.min_stay > 0 && ` · min ${r.min_stay} nights`}
              </div>
            </div>
            <button onClick={() => del(r.id)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
          </div>
        ))}
        {rules.length === 0 && <p className="text-sm text-stone-400">No rate rules yet. Base nightly rates apply.</p>}
      </div>
      </div>
    </div>
  );
}
